import { OrderAssistOption, PDFAcceptingNewPatientsType } from '../types';
import addressFormat from './addressFormatter';
import { capitalizeAsName, isProviderResult } from './general';
import { getSelectedItemAddress } from './search/getSelectedAddress';
import { isNull } from 'lodash-es';

const formatForClipboard = ({
  option,
  supportScoreRatingBadges,
  supportOpeningHours,
  supportNetworkStatusBadge,
  supportAcceptingNewPatientsInCard,
  displayDistanceInExportDetails,
}: {
  option: OrderAssistOption;
  supportNetworkStatusBadge?: boolean;
  supportScoreRatingBadges?: boolean;
  supportOpeningHours?: boolean;
  supportAcceptingNewPatientsInCard?: boolean;
  displayDistanceInExportDetails?: boolean;
}): string => {
  const address = getSelectedItemAddress(option);
  if (!address) return '';
  const fullAddress = addressFormat(address);
  const officeName = address?.officeName ? `Office name: ${address.officeName}\n` : '';

  let sharedDetails =
    `Specialties: ${
      option.details.specialties?.length
        ? option.details.specialties
            .filter((s) => s.description)
            .map((s) => capitalizeAsName(s.description))
            .join(', ')
        : 'N/A'
    }\n` +
    `${officeName}` +
    `Address: ${fullAddress || 'N/A'}\n` +
    `Phone: ${address?.phone || 'N/A'}\n`;

  if (address.fax) {
    sharedDetails += `Fax: ${address.fax}\n`;
  }

  sharedDetails += `NPI: ${option.details.npi}`;

  if (supportAcceptingNewPatientsInCard && !isNull(address.acceptNewPatients)) {
    sharedDetails += `\nAccepting new patients: ${
      address.acceptNewPatients ? PDFAcceptingNewPatientsType.Yes : PDFAcceptingNewPatientsType.No
    }`;
  }

  if (
    displayDistanceInExportDetails &&
    address?.distance &&
    Number.isFinite(Number(address?.distance))
  ) {
    sharedDetails += `\nDistance: ${Number.parseFloat(address.distance).toFixed(2)} mi`;
  }

  if (supportOpeningHours && address?.openingHours?.length) {
    const openingHoursArray = address.openingHours.map(
      (openingHour) => `${openingHour.daysRange}: ${openingHour.open} - ${openingHour.close}`,
    );
    sharedDetails += `\nOpening hours: ${
      openingHoursArray?.length ? '\n' + openingHoursArray.join('\n') : 'N/A'
    }`;
  }

  if (supportScoreRatingBadges && option.teladocGarnerExtraInfo?.score) {
    sharedDetails += `\nScore: ${option.teladocGarnerExtraInfo?.score}`;
  }

  if (supportScoreRatingBadges && option.teladocGarnerExtraInfo?.rating) {
    sharedDetails += `\nPatient rating: ${option.teladocGarnerExtraInfo?.rating}`;
  }

  if (supportNetworkStatusBadge) {
    sharedDetails += `\nNetwork status: ${
      option.teladocGarnerExtraInfo?.networkStatus ? 'In network' : 'Out of network'
    }`;
  }

  if (isProviderResult(option)) {
    let providerDetails = `Provider name: ${capitalizeAsName(
      `${option.details.complexName.firstName} ${option.details.complexName.lastName}`,
    )}\n`;
    if (option.details.acoName) {
      providerDetails += `Organization: ${capitalizeAsName(option.details.acoName)}\n`;
    }
    return providerDetails + sharedDetails;
  }

  const facilityName = option.details.name || option.details.acoName;
  const facilityDetails = !facilityName ? '' : `Facility name: ${capitalizeAsName(facilityName)}\n`;
  return facilityDetails + sharedDetails;
};

export default formatForClipboard;
